<template>
  <error-boundary error-key="tariffScheduleServiceGroups">
    <div class="d-flex align-center">
      <div v-if="isEditMode" class="paragraph-m text-secondary">Настройте параметры использования групп услуг</div>

      <template v-else>
        <e-alert
          v-if="tariffServiceGroups?.operations.hasMaximumServiceGroups"
          class="w-100 mr-md-6 mb-6"
          variant="info"
        >
          <div>
            <div class="paragraph-s text-secondary">Вы создали максимальное количество <b>Групп услуг</b></div>
          </div>
        </e-alert>
        <e-btn
          v-else-if="isOperationVisible(tariffServiceGroups?.operations.add)"
          prepend-icon-src="plus"
          variant="secondary"
          class="mb-6"
          :disabled="isOperationDisabled(tariffServiceGroups?.operations.add)"
          @click="openCreateServiceGroupDialog"
        >
          Добавить Группу услуг
        </e-btn>
      </template>
      <v-spacer />
      <mobile-floating-bar
        v-if="isOperationVisible(tariffServiceGroups?.operations?.edit)"
        class="d-flex justify-end mb-sm-6"
      >
        <operation-button
          v-if="!isEditMode"
          prepend-icon-src="options"
          variant="primary"
          :operation="tariffServiceGroups?.operations?.edit"
          :loading="isEditTariffScheduleServiceGroupListStartLoading"
          @click="startEdit"
        >
          Настроить
        </operation-button>
        <template v-else>
          <confirmation-dialog :action="stopEdit" confirm-text="Выйти без сохранения изменений">
            <template #activator>
              <e-btn variant="secondary">Отмена</e-btn>
            </template>
            <div class="h3 mb-6">Выйти из режима редактирования</div>
            <div class="paragraph-m text-secondary">Все внесённые изменения не будут сохранены.</div>
          </confirmation-dialog>
          <e-btn
            :loading="isEditTariffScheduleServiceGroupListUpdateLoading"
            variant="primary"
            class="ml-4"
            @click="submit"
          >
            Сохранить
          </e-btn>
        </template>
      </mobile-floating-bar>
    </div>

    <e-form v-model="isValid" ref="formRef">
      <e-data-items
        v-if="tariffServiceGroups"
        :items="serviceGroups"
        :headers="headers"
        :interactive="!isEditMode"
        :loading="isLoading"
        @on-row-click="openServiceGroup"
      >
        <template v-slot:item.name[0].value="{ value, item }">
          <div>
            {{ value }}
            <help-tooltip v-if="item.description.length" :icon-size="16">
              {{ item.description[0]?.value }}
            </help-tooltip>
          </div>
        </template>
        <template v-slot:[`item.services`]="{ value }">
          <div>
            <div v-for="(service, index) in value" :key="index" class="service-item">
              <span>{{ service.shortName }}</span>
              <img 
                v-if="service.isFirst" 
                :src="firstServiceIcon"
                alt="Первая услуга"
                title="Первая услуга"
              />
              <img 
                v-if="service.isLast" 
                :src="lastServiceIcon"
                alt="Последняя услуга"
                title="Последняя услуга"
              />
            </div>
          </div>
        </template>
        <template v-slot:[`item.defaultAvailable`]="{ value, item }">
          <e-checkbox v-if="isEditMode" v-model="item.defaultAvailable" />
          <div v-else-if="value">
            <e-icon name="check" />
          </div>
        </template>
        <template v-slot:[`item.orderPeriod.minOrderPeriod`]="{ value, item }">
          <e-input
            v-if="isEditMode"
            v-model:number-value="item.orderPeriod.minOrderPeriod"
            :maska="orderPeriodMask"
            :rules="[required(), min(minOrderPeriod), max(maxOrderPeriod, `Максимум ${maxOrderPeriod}`)]"
          />
          <div v-else>
            {{ value }}
          </div>
        </template>
        <template v-slot:[`item.orderPeriod.maxOrderPeriod`]="{ value, item }">
          <e-input
            v-if="isEditMode && (item.showInput || value)"
            v-model:number-value="item.orderPeriod.maxOrderPeriod"
            :maska="orderPeriodMask"
            :rules="[required(), max(maxOrderPeriod, `Максимум ${maxOrderPeriod}`)]"
            :error-messages="
              min(item.orderPeriod.minOrderPeriod, 'Не может быть меньше Мин. срока')(item.orderPeriod.maxOrderPeriod)
            "
          />
          <div
            v-else
            :class="{ link: !value && isEditMode }"
            @click="
              item.showInput = true;
              item.orderPeriod.maxOrderPeriod = 1;
            "
          >
            {{ value || 'Не ограничен' }}
          </div>
        </template>
        <template v-slot:[`item.isNeedConfirm`]="{ value, item }">
          <e-checkbox v-if="isEditMode" v-model="item.isNeedConfirm" />
          <div v-else-if="value">
            <e-icon name="check" />
          </div>
        </template>
      </e-data-items>
    </e-form>

    <create-service-group-dialog
      v-if="activeTariff"
      v-model="isCreateServiceGroupDialogOpened"
      :tariff-schedule-cid="activeTariff.tariffScheduleCid"
      :hotel-set-cid="activeTariff.hotelSetCid"
      :show-confirmation="activeTariff?.status !== TariffScheduleStatus.Draft"
    />
    <service-group-info-dialog
      v-if="activeTariff && activeServiceGroup"
      v-model="isServiceGroupDialogOpened"
      :tariff-schedule-cid="activeTariff.tariffScheduleCid"
      :hotel-set-cid="activeTariff.hotelSetCid"
      :service-group-cid="activeServiceGroup.serviceGroupCid"
    />
  </error-boundary>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import type { EDataItemsOnRowClickItem } from '@/core/types/components/components';
import type { DataTableHeaders } from '@/core/types/components/table';
import type { ArrayElementType } from '@/core/types/gengeric';
import OperationButton from '@/core/components/buttons/OperationButton.vue';
import MobileFloatingBar from '@/core/components/common/MobileFloatingBar.vue';
import EDataItems from '@/core/components/common/data-items/EDataItems.vue';
import ConfirmationDialog from '@/core/components/modals/ConfirmationDialog.vue';
import { EAlert, EBtn, ECheckbox, EForm, EIcon, EInput, HelpTooltip } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import { orderPeriodMask } from '@/core/consts/masks';
import useConfirmationDialog from '@/core/store/useConfirmationDialog';
import { isOperationDisabled, isOperationVisible } from '@/core/utils/permissions';
import { max, min, required } from '@/core/utils/validation';
import { TariffScheduleStatus } from '@/graphql/generated';
import ErrorBoundary from '@/parts/errors/components/ErrorBoundary.vue';
import CreateServiceGroupDialog from '@/parts/tariffs/components/ServiceGroups/CreateServiceGroupDialog.vue';
import ServiceGroupInfoDialog from '@/parts/tariffs/components/ServiceGroups/ServiceGroupInfoDialog.vue';
import {
  type TariffServiceGroupListItem,
  type TariffServiceGroups,
  useServiceGroupsStore,
} from '@/parts/tariffs/store/serviceGroups';
import { useTariffsStore } from '@/parts/tariffs/store/tariffs';
import { maxOrderPeriod, minOrderPeriod } from '@/parts/tariffs/utils/consts';
import firstServiceIcon from '@/assets/img/pages/tariffs/service-groups/first-service.svg';
import lastServiceIcon from '@/assets/img/pages/tariffs/service-groups/last-service.svg';

const { activeTariff } = useTariffsStore();

const {
  getTariffServiceGroups,
  tariffServiceGroups,
  editTariffScheduleServiceGroupListStart,
  isEditTariffScheduleServiceGroupListStartLoading,
  editTariffScheduleServiceGroupListCancel,
  editTariffScheduleServiceGroupListUpdate,
  isEditTariffScheduleServiceGroupListUpdateLoading,
} = useServiceGroupsStore();
const { showConfirmationDialog } = useConfirmationDialog();

const serviceGroups = computed<(TariffServiceGroupListItem & { showInput: boolean })[]>(
  () =>
    tariffServiceGroups.value?.serviceGroups?.map((item) => {
      const newItem = item as TariffServiceGroupListItem & { showInput: boolean };
      newItem.showInput = false;

      return newItem;
    }) || [],
);

const isEditMode = ref(false);

const isCreateServiceGroupDialogOpened = ref(false);

const openCreateServiceGroupDialog = () => {
  isCreateServiceGroupDialogOpened.value = true;
};

const activeServiceGroup = ref<ArrayElementType<TariffServiceGroups['serviceGroups']>>();
const isServiceGroupDialogOpened = ref(false);

const openServiceGroup = async (
  e: Event,
  item: EDataItemsOnRowClickItem<ArrayElementType<TariffServiceGroups['serviceGroups']>>,
) => {
  if (isEditMode.value) {
    return;
  }

  activeServiceGroup.value = item.item;
  await nextTick();

  isServiceGroupDialogOpened.value = true;
};

const startEdit = async () => {
  if (!activeTariff.value) {
    return;
  }

  const result = await editTariffScheduleServiceGroupListStart(activeTariff.value.tariffScheduleCid);

  if (!result?.errors) {
    isEditMode.value = true;
  }
};

const stopEdit = async () => {
  await editTariffScheduleServiceGroupListCancel();

  await getTariffServiceGroups();

  isEditMode.value = false;
};

const isValid = ref(false);
const formRef = ref();

const submit = async () => {
  formRef.value?.validate();

  if (!isValid.value || !activeTariff.value) {
    return;
  }

  const payload = {
    tariffScheduleCid: activeTariff.value.tariffScheduleCid,
    serviceGroups: tariffServiceGroups.value!.serviceGroups!.map((s) => ({
      serviceGroupCid: s.serviceGroupCid,
      defaultAvailable: s.defaultAvailable,
      isNeedConfirm: s.isNeedConfirm,
      orderPeriod: {
        minOrderPeriod: s.orderPeriod.minOrderPeriod,
        maxOrderPeriod: s.orderPeriod.maxOrderPeriod,
      },
    })),
  };

  const result = await editTariffScheduleServiceGroupListUpdate(payload);

  if (!result?.errors) {
    isEditMode.value = false;
  }
};

const headers: DataTableHeaders = [
  {
    key: 'name[0].value',
    title: 'Наименование',
  },
  {
    key: 'services',
    title: 'Включённые услуги',
    sortRaw: (v1: TariffServiceGroupListItem, v2: TariffServiceGroupListItem) =>
      v2.services.map((s) => s.shortName).join(', ') > v1.services.map((s) => s.shortName).join(', ') ? -1 : 1,
  },
  {
    key: 'defaultAvailable',
    title: 'Используется',
    align: 'start',
    width: 120,
    sortable: false,
  },
  {
    key: 'orderPeriod.minOrderPeriod',
    title: 'Мин. срок',
    align: 'start',
    width: 180,
    sortable: false,
  },
  {
    key: 'orderPeriod.maxOrderPeriod',
    title: 'Макс. срок',
    align: 'start',
    width: 180,
    sortable: false,
  },
  {
    key: 'isNeedConfirm',
    title: 'Под запрос',
    align: 'start',
    width: 120,
    sortable: false,
  },
];

onBeforeUnmount(() => {
  if (isEditMode.value) {
    void editTariffScheduleServiceGroupListCancel();
  }
});

onBeforeRouteLeave((to, from, next) => {
  if (isEditMode.value) {
    showConfirmationDialog({ action: next });
  } else {
    next();
  }
});

const { isLoading } = useFetch(async () => {
  await getTariffServiceGroups();
});
</script>

<style scoped lang="scss">
.service-item {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
